import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { destinationsAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MapPin,
  Building,
  GraduationCap,
  Briefcase,
  ArrowRight,
  Loader2,
  Globe,
  Users,
  BookOpen,
  Clock,
  Check,
  ChevronRight,
  Plane,
  Home,
  DollarSign,
  Scale,
  Award
} from 'lucide-react';

const DestinationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await destinationsAPI.getAll();
      setDestinations(response.data || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Additional destination details
  const destinationDetails = {
    'United Kingdom': {
      flag: '🇬🇧',
      highlights: ['Post-study work visa (2 years)', 'Part-time work allowed (20 hrs/week)', 'NHS healthcare access'],
      language: 'English',
      intakes: ['September (Main)', 'January', 'May'],
      costOfLiving: '£1,000-£1,500/month',
      workPermit: 'Graduate Route Visa'
    },
    'United States': {
      flag: '🇺🇸',
      highlights: ['OPT work authorization (1-3 years)', 'Campus employment allowed', 'Diverse program options'],
      language: 'English',
      intakes: ['Fall (August)', 'Spring (January)', 'Summer'],
      costOfLiving: '$1,200-$2,500/month',
      workPermit: 'OPT/CPT'
    },
    'Canada': {
      flag: '🇨🇦',
      highlights: ['Post-graduation work permit (3 years)', 'Pathway to permanent residence', 'Affordable education'],
      language: 'English/French',
      intakes: ['September', 'January', 'May'],
      costOfLiving: 'CAD 1,000-$1,500/month',
      workPermit: 'PGWP'
    },
    'Ireland': {
      flag: '🇮🇪',
      highlights: ['Stay back option (2 years)', 'Tech hub of Europe', 'Friendly community'],
      language: 'English',
      intakes: ['September', 'January'],
      costOfLiving: '€1,000-€1,500/month',
      workPermit: 'Third Level Graduate Scheme'
    },
    'France': {
      flag: '🇫🇷',
      highlights: ['Low tuition fees', 'Rich cultural heritage', 'Work rights during study'],
      language: 'French/English',
      intakes: ['September', 'January'],
      costOfLiving: '€800-€1,500/month',
      workPermit: 'Talent Passport'
    },
    'Germany': {
      flag: '🇩🇪',
      highlights: ['Low/No tuition at public universities', '18-month Job Seeker Visa', 'Strong economy & job market'],
      language: 'German/English',
      intakes: ['October (Winter)', 'April (Summer)'],
      costOfLiving: '€800-€1,200/month',
      workPermit: '18-month Job Seeker Visa'
    },
    'Netherlands': {
      flag: '🇳🇱',
      highlights: ['High English proficiency', 'Orientation Year Visa', 'Innovation hub of Europe'],
      language: 'Dutch/English',
      intakes: ['September', 'February'],
      costOfLiving: '€900-€1,400/month',
      workPermit: 'Orientation Year Visa'
    }
  };

  const getDetails = (country) => destinationDetails[country] || {
    flag: '🌍',
    highlights: ['Quality education', 'International exposure', 'Career opportunities'],
    language: 'English',
    intakes: ['September', 'January'],
    costOfLiving: 'Varies',
    workPermit: 'Student visa'
  };

  const toggleCompareSelection = (dest) => {
    if (selectedForCompare.find(d => d.id === dest.id)) {
      setSelectedForCompare(selectedForCompare.filter(d => d.id !== dest.id));
    } else if (selectedForCompare.length < 4) {
      setSelectedForCompare([...selectedForCompare, dest]);
    }
  };

  const handleCompare = () => {
    if (selectedForCompare.length >= 2) {
      setShowCompareModal(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section - Soft Muted Colors */}
        <div className="bg-gradient-to-br from-[#e9ecef] via-[#f1f3f5] to-[#f8f9fa] dark:from-slate-800 dark:via-slate-800 dark:to-slate-900 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-teal-100 text-teal-700 border-0 mb-6 px-4 py-2 text-sm font-medium">
                <Globe className="w-4 h-4 mr-2" />
                {destinations.length} Top Destinations Worldwide
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl font-semibold text-gray-800 dark:text-white mb-6 leading-tight">
                Your Global Education
                <span className="block text-teal-700 dark:text-teal-400 mt-2">
                  Starts Here
                </span>
              </h1>
              
              <p className="text-base text-gray-500 dark:text-gray-400 mb-10 leading-relaxed">
                Compare study destinations, understand visa requirements, and find the perfect country for your academic and career goals.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="bg-white/80 dark:bg-slate-800 rounded-2xl p-5 border border-gray-200 dark:border-slate-700">
                  <p className="text-xl font-semibold text-teal-700 dark:text-teal-400">500+</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Partner Universities</p>
                </div>
                <div className="bg-white/80 dark:bg-slate-800 rounded-2xl p-5 border border-gray-200 dark:border-slate-700">
                  <p className="text-xl font-semibold text-teal-700 dark:text-teal-400">{destinations.length}</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Countries</p>
                </div>
                <div className="bg-white/80 dark:bg-slate-800 rounded-2xl p-5 border border-gray-200 dark:border-slate-700">
                  <p className="text-xl font-semibold text-teal-700 dark:text-teal-400">10K+</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Students Placed</p>
                </div>
                <div className="bg-white/80 dark:bg-slate-800 rounded-2xl p-5 border border-gray-200 dark:border-slate-700">
                  <p className="text-xl font-semibold text-teal-700 dark:text-teal-400">95%</p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Visa Success</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          {/* Section Header with Compare Toggle - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-8 transition-colors">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Explore Destinations</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                  {compareMode 
                    ? `Select up to 4 destinations to compare (${selectedForCompare.length} selected)` 
                    : 'Click on any country to explore, or compare multiple destinations'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                onClick={() => {
                  setCompareMode(!compareMode);
                  if (compareMode) setSelectedForCompare([]);
                }}
                variant={compareMode ? "default" : "outline"}
                className={compareMode 
                  ? "bg-teal-600 text-white hover:bg-teal-700" 
                  : "border-2 border-gray-300 text-gray-600 hover:bg-gray-100"
                }
                data-testid="compare-toggle-btn"
              >
                <Scale className="w-4 h-4 mr-2" />
                {compareMode ? 'Exit Compare' : 'Compare Destinations'}
              </Button>
              {compareMode && selectedForCompare.length >= 2 && (
                  <Button
                    onClick={handleCompare}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="show-compare-btn"
                  >
                    Compare ({selectedForCompare.length})
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Destinations Grid - Soft Card Layout */}
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6">
            {destinations.map((dest) => {
              const details = getDetails(dest.country);
              const isSelected = selectedForCompare.find(d => d.id === dest.id);
              return (
                <div 
                  key={dest.id}
                  className={`group bg-white dark:bg-slate-800 rounded-2xl overflow-hidden border-2 transition-all duration-300 hover:shadow-lg ${
                    compareMode 
                      ? isSelected 
                        ? 'border-teal-500 ring-4 ring-teal-100 dark:ring-teal-900' 
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 cursor-pointer'
                      : 'border-gray-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-teal-500'
                  }`}
                  onClick={() => compareMode && toggleCompareSelection(dest)}
                  data-testid={`destination-${dest.id}`}
                >
                  {/* Compare Checkbox Indicator */}
                  {compareMode && (
                    <div className="absolute top-4 left-4 z-10">
                      <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all shadow-md ${
                        isSelected 
                          ? 'bg-teal-600 border-teal-600' 
                          : 'bg-white border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-gray-800 dark:text-white" />}
                      </div>
                    </div>
                  )}
                  
                  {/* Country Image */}
                  <div className="relative h-44 overflow-hidden">
                    <img 
                      src={dest.image_url} 
                      alt={dest.country}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-5 right-5">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl drop-shadow-lg">{details.flag}</span>
                        <div>
                          <h3 className="text-xl font-semibold text-white drop-shadow-md">{dest.country}</h3>
                          <p className="text-white/80 text-sm">{dest.popular_cities?.slice(0, 2).join(', ')}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-5">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div className="bg-gray-100 dark:bg-slate-700 rounded-xl p-3">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
                          <GraduationCap className="w-3.5 h-3.5" />
                          Avg. Tuition
                        </div>
                        <p className="text-teal-700 dark:text-teal-400 font-semibold text-sm">{dest.avg_tuition_range}</p>
                      </div>
                      <div className="bg-gray-100 dark:bg-slate-700 rounded-xl p-3">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
                          <Home className="w-3.5 h-3.5" />
                          Living Cost
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 font-semibold text-sm">{details.costOfLiving}</p>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mb-5">
                      <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider font-semibold mb-2">Key Benefits</p>
                      <ul className="space-y-2">
                        {details.highlights.slice(0, 3).map((highlight, idx) => (
                          <li key={`${`+var_name+`}-${idx}`} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <div className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Quick Info Row */}
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-5 pb-4 border-b border-gray-100 dark:border-slate-600">
                      <div className="flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                        {details.language}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Building className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                        {dest.universities_count}+ Universities
                      </div>
                    </div>

                    {/* Intake Info */}
                    <div className="mb-5">
                      <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider font-semibold mb-2">Intake Seasons</p>
                      <div className="flex flex-wrap gap-1.5">
                        {details.intakes.map((intake, idx) => (
                          <Badge key={`${`+var_name+`}-${idx}`} className="bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-300 border-0 font-medium text-xs">
                            {intake}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <Link to={`/courses?country=${dest.country.split(' ')[0]}`}>
                      <Button 
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl h-11" 
                        data-testid={`explore-${dest.id}`}
                      >
                        Explore Courses
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* More Countries Coming Soon Message */}
          <div className="mt-10 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-2xl p-6 text-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <Globe className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-1">
                  More Countries Coming Soon!
                </h3>
                <p className="text-amber-700 dark:text-amber-300 text-sm">
                  We're expanding our services. Currently, select from <strong>United Kingdom</strong> or <strong>Ireland</strong> for full course listings.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Comparison Table */}
          <div className="mt-20">
            <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Quick Comparison</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Compare key factors across all destinations at a glance</p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                      <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-semibold">Country</th>
                      <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-semibold">Tuition Range</th>
                      <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-semibold">Living Cost</th>
                      <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-semibold">Work Rights</th>
                      <th className="text-left py-4 px-6 text-gray-600 dark:text-gray-300 font-semibold">Post-Study Visa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinations.map((dest, idx) => {
                      const details = getDetails(dest.country);
                      return (
                        <tr key={dest.id} className={`border-b border-gray-100 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700/30'}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <span className="text-xl">{details.flag}</span>
                              <span className="text-gray-800 dark:text-white font-semibold">{dest.country}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-teal-700 dark:text-teal-400 font-semibold">{dest.avg_tuition_range}</td>
                          <td className="py-4 px-6 text-gray-600 dark:text-gray-300">{details.costOfLiving}</td>
                          <td className="py-4 px-6">
                            <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 border-0 font-medium">Allowed</Badge>
                          </td>
                          <td className="py-4 px-6 text-gray-600 dark:text-gray-300">{details.workPermit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Why Choose Us Section */}
          <div className="mt-20">
            <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-slate-600">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Why Study Abroad with H E Direct?</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">We guide you through every step of your international education journey</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-4 gap-5">
                {[
                  { icon: Users, title: 'Expert Counseling', desc: 'Personalized guidance from experienced education consultants' },
                  { icon: Plane, title: 'Visa Assistance', desc: '95% visa success rate with complete documentation support' },
                  { icon: DollarSign, title: 'Scholarship Help', desc: 'Guidance on scholarships and financial aid options' },
                  { icon: Briefcase, title: 'Career Support', desc: 'Post-study work visa guidance and career counseling' }
                ].map((item, idx) => (
                  <div key={`${`+var_name+`}-${idx}`} className="bg-gray-50 dark:bg-slate-700 rounded-2xl p-6 text-center border border-gray-200 dark:border-slate-600 hover:border-teal-300 dark:hover:border-teal-500 hover:shadow-md transition-all duration-300">
                    <div className="w-12 h-12 rounded-2xl bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-6 h-6 text-teal-700 dark:text-teal-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-2">{item.title}</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 bg-teal-700 rounded-3xl p-10 md:p-12 text-center">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-teal-100 max-w-xl mx-auto mb-8 text-sm">
              Create your profile and get personalized course recommendations based on your preferences and budget
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button className="bg-white text-teal-700 hover:bg-gray-100 font-medium text-base px-8 h-12 rounded-full">
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/courses">
                <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 text-base px-8 h-12 rounded-full">
                  Browse All Courses
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Compare Destinations Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white text-2xl flex items-center gap-3">
              <Scale className="w-6 h-6 text-teal-600 dark:text-teal-400" />
              Compare Destinations
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-x-auto mt-6">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-600">
                  <th className="text-left py-4 px-4 text-gray-500 dark:text-gray-400 font-semibold w-40">Criteria</th>
                  {selectedForCompare.map(dest => {
                    const details = getDetails(dest.country);
                    return (
                      <th key={dest.id} className="text-center py-4 px-4 min-w-[180px]">
                        <div className="flex flex-col items-center">
                          <span className="text-4xl mb-2">{details.flag}</span>
                          <span className="text-gray-800 dark:text-white font-semibold">{dest.country}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 dark:border-slate-600">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Living Cost</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => {
                    const details = getDetails(dest.country);
                    return (
                      <td key={dest.id} className="text-center py-4 px-4">
                        <span className="text-gray-800 dark:text-white font-semibold">{details.costOfLiving}</span>
                      </td>
                    );
                  })}
                </tr>
                
                <tr className="border-b border-gray-100 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Post-Study Visa</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => {
                    const details = getDetails(dest.country);
                    return (
                      <td key={dest.id} className="text-center py-4 px-4">
                        <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 border-0">{details.workPermit}</Badge>
                      </td>
                    );
                  })}
                </tr>
                
                <tr className="border-b border-gray-100 dark:border-slate-600">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Avg. Tuition</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => (
                    <td key={dest.id} className="text-center py-4 px-4">
                      <span className="text-gray-800 dark:text-white font-semibold">{dest.avg_tuition_range || 'Varies'}</span>
                    </td>
                  ))}
                </tr>
                
                <tr className="border-b border-gray-100 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Partner Universities</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => (
                    <td key={dest.id} className="text-center py-4 px-4">
                      <span className="text-2xl font-semibold text-teal-700 dark:text-teal-400">{dest.universities_count || 0}+</span>
                    </td>
                  ))}
                </tr>
                
                <tr className="border-b border-gray-100 dark:border-slate-600">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Language</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => {
                    const details = getDetails(dest.country);
                    return (
                      <td key={dest.id} className="text-center py-4 px-4">
                        <span className="text-gray-600 dark:text-gray-300">{details.language}</span>
                      </td>
                    );
                  })}
                </tr>
                
                <tr className="border-b border-gray-100 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/30">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Main Intakes</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => {
                    const details = getDetails(dest.country);
                    return (
                      <td key={dest.id} className="text-center py-4 px-4">
                        <div className="flex flex-wrap justify-center gap-1">
                          {details.intakes.map((intake, idx) => (
                            <Badge key={`${`+var_name+`}-${idx}`} className="bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-300 border-0 text-xs">
                              {intake}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
                
                <tr>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-gray-600 dark:text-gray-300 font-medium">Key Benefits</span>
                    </div>
                  </td>
                  {selectedForCompare.map(dest => {
                    const details = getDetails(dest.country);
                    return (
                      <td key={dest.id} className="py-4 px-4">
                        <ul className="space-y-2">
                          {details.highlights.map((highlight, idx) => (
                            <li key={`${`+var_name+`}-${idx}`} className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 dark:text-gray-300">{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="flex justify-between items-center pt-6 mt-4 border-t border-gray-200 dark:border-slate-600">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Comparing {selectedForCompare.length} destinations
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCompareModal(false)}
                className="border-gray-200 text-gray-600 dark:text-gray-300"
              >
                Close
              </Button>
              <Link to="/courses">
                <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold">
                  Explore Courses
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default DestinationsPage;
